import gsap from "gsap";

/**
 * Manages a group of checkboxes with animated behavior, selection limits, and average display.
 */
export default class Checkboxes {
  constructor(checkboxElements, options = {}) {
    if (!checkboxElements || checkboxElements.length === 0) {
      throw new Error("Checkbox elements are required");
    }
    this.checkboxElements = Array.from(checkboxElements);
    this.limit = options.limit || 3;
    this.onChange = options.onChange || (() => {});

    this.rotations = [220, 200, 180, 160, 140, 0, 20];
    this.selectionOrder = []; // Tracks the order of checkbox selection
    this.checkboxData = []; // Stores checkbox-specific data
    this.done = false; // Indicates if the selection process is complete
    this.init();
  }

  init() {
    this.shuffleRotations();
    this.checkboxElements.forEach((element, index) => {
      const input = element.querySelector(".checkbox__input");
      const icon = element.querySelector(".checkbox__icon");
      const iconSVG = icon.querySelector("svg");
      const value = element.querySelector(".checkbox__value");
      const average = element.querySelector(".checkbox__average");

      if (!input || !icon || !value) {
        throw new Error("Required elements are missing in checkbox");
      }

      this.checkboxData.push({ element, input, icon, iconSVG, value, average });

      this.applyRotation(iconSVG, index);
      input.addEventListener("change", (event) => this.handleChange(event));
      input.addEventListener("showAverage", (event) =>
        this.showAverage(event, element)
      );

      // Check if the checkbox is already checked
      if (input.checked) {
        this.selectionOrder.push(element);
      }
    });

    // Sort the selectionOrder based on the order of elements in checkboxElements
    this.selectionOrder.sort(
      (a, b) =>
        this.checkboxElements.indexOf(a) - this.checkboxElements.indexOf(b)
    );

    this.updateAllUI();
    this.animateEntrance();

    // Check if we've already reached the limit
    this.checkLimitReached();
  }

  /**
   * Shuffles the rotation angles for checkbox icons.
   */
  shuffleRotations() {
    for (let i = this.rotations.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.rotations[i], this.rotations[j]] = [
        this.rotations[j],
        this.rotations[i],
      ];
    }
  }

  /**
   * Applies a rotation to a checkbox icon.
   * @param {Element} element - The SVG element to rotate.
   * @param {number} index - The index of the rotation to apply.
   */
  applyRotation(element, index) {
    gsap.set(element, {
      rotation: this.rotations[index % this.rotations.length],
      transformOrigin: "50% 50%",
    });
  }

  /**
   * Handles checkbox change events.
   * @param {Event} event - The change event.
   */
  handleChange(event) {
    if (this.done) {
      event.preventDefault();
      return;
    }

    const input = event.target;
    const checkboxElement = input.closest(".checkbox");

    if (input.checked) {
      this.selectionOrder.push(checkboxElement);
    } else {
      const index = this.selectionOrder.indexOf(checkboxElement);
      if (index > -1) {
        this.selectionOrder.splice(index, 1);
      }
    }

    this.checkLimitReached();
    this.updateAllUI();
    this.onChange(this.getCheckedValues());
  }

  /**
   * Checks if the selection limit has been reached and applies appropriate actions.
   */
  checkLimitReached() {
    const checkedCount = this.getCheckedCount();
    if (checkedCount >= this.limit) {
      this.limitReached();
    } else {
      this.limitNotReached();
    }
  }

  /**
   * Applies disabled state to unchecked checkboxes when limit is reached.
   */
  limitReached() {
    this.checkboxElements.forEach((element) => {
      const input = element.querySelector(".checkbox__input");
      if (!input.checked) {
        input.disabled = true;
        input.dataset.limitReached = true;
      }
    });
  }

  /**
   * Removes disabled state from all checkboxes when below limit.
   */
  limitNotReached() {
    this.checkboxElements.forEach((element) => {
      const input = element.querySelector(".checkbox__input");
      input.disabled = false;
      delete input.dataset.limitReached;
    });
  }

  /**
   * Updates the UI for all checkboxes.
   */
  updateAllUI() {
    this.checkboxElements.forEach((element) => {
      const input = element.querySelector(".checkbox__input");
      const icon = element.querySelector(".checkbox__icon");
      const value = element.querySelector(".checkbox__value");

      if (input.checked) {
        const orderIndex = this.selectionOrder.indexOf(element) + 1;
        value.textContent = orderIndex;
      } else {
        value.textContent = "";
      }

      gsap.to(value, {
        scale: input.checked ? 1 : 0,
        opacity: input.checked ? 1 : 0,
        duration: 0.25,
        ease: "back.out(1.7)",
      });
    });
  }

  /**
   * Sets checkboxes as checked based on provided values.
   * @param {Array<string>} values - The values of checkboxes to check.
   */
  setCheckedByValues(values) {
    if (this.done) return;

    this.selectionOrder = [];
    this.checkboxElements.forEach((el) => {
      const input = el.querySelector(".checkbox__input");
      if (values.includes(input.value)) {
        input.checked = true;
        this.selectionOrder.push(el);
      } else {
        input.checked = false;
      }
    });
    this.updateAllUI();
    this.checkLimitReached();
    this.onChange(this.getCheckedValues());
  }

  /**
   * Animates the entrance of checkboxes.
   */
  animateEntrance() {
    gsap.to(this.checkboxElements, {
      autoAlpha: 1,
      duration: 0.5,
      ease: "power1.inOut",
      stagger: 0.05,
      clearProps: "opacity",
    });
  }

  /**
   * Gets the count of checked checkboxes.
   * @returns {number} The number of checked checkboxes.
   */
  getCheckedCount() {
    return this.checkboxElements.filter(
      (el) => el.querySelector(".checkbox__input").checked
    ).length;
  }

  /**
   * Gets the values of checked checkboxes.
   * @returns {Array<string>} An array of checked checkbox values.
   */
  getCheckedValues() {
    return this.checkboxElements
      .filter((el) => el.querySelector(".checkbox__input").checked)
      .map((el) => el.querySelector(".checkbox__input").value);
  }

  /**
   * Shows the average value for a specific checkbox and completes the selection process.
   * @param {CustomEvent} event - The custom event with the average value
   * @param {Element} checkboxElement - The checkbox element to update
   */
  showAverage(event, checkboxElement) {
    if (this.done) return; // Prevent showing averages multiple times

    let average = event.detail.average;

    // Format the average value to % and round to 0 decimal places
    average = `${Math.round(average)}%`;

    // Find the corresponding checkbox data
    const checkboxData = this.checkboxData.find(
      (data) => data.element === checkboxElement
    );

    if (checkboxData && checkboxData.average) {
      // Update the average element's text content
      checkboxData.average.textContent = average;

      gsap
        .timeline({
          defaults: {
            duration: 0.35,
            ease: 'back.out'
          },
        })
        .to(checkboxData.element, {
          autoAlpha: 1,
          scale: 1
        })
        .to(
          checkboxData.value,
          {
            autoAlpha: 0,
            scale: 0
          },
          "<"
        )
        .from(checkboxData.average, {
          autoAlpha: 0,
          scale: 0,
        }, '<');

      // Disable the checkbox
      checkboxData.input.disabled = true;
    }

    // Check if all averages have been shown
    if (this.checkboxData.every((data) => data.average.textContent)) {
      this.done = true;
      this.disableAllCheckboxes();
    }
  }

  /**
   * Disables all checkboxes and applies a visual indication.
   */
  disableAllCheckboxes() {
    this.checkboxData.forEach((data) => {
      data.input.disabled = true;
    });
  }

  /**
   * Removes event listeners and cleans up resources.
   */
  destroy() {
    this.checkboxData.forEach(({ input }) => {
      input.removeEventListener("change", this.handleChange);
      input.removeEventListener("showAverage", this.showAverage);
    });
  }
}
